import React from 'react';
import { Button, Tooltip } from '@getro/rombo';
import { Flex, Box, Text, Image } from 'rebass/styled-components';
import { Download, ListFilter, Plus } from 'lucide-react';
import PropTypes from 'prop-types';
import { Switch, Route, useHistory, NavLink, useLocation } from 'react-router-dom';

import { getRowClass, processCellForClipboard } from 'helpers/cell';
import {
  CompanyRowRenderer,
  ContactNameRenderer,
  CompanyNameRenderer,
  ContactOfRenderer,
} from 'components/molecules/renderers';
import IconHandEmptyIcon from '../../assets/icon-hand-empty.svg';
import DataTable from '../../components/molecules/agGrid';
import { CompanyFilters } from '../../components/organisms/companyFilters';
import { ContactDetail } from '../../components/organisms/contactDetail';
import { trackEvent } from '../../helpers/analytics';
import { useCompanyList } from './hooks/useCompanyList';
import ExportListCompanies from './exportListCompanies';
import { ListSettings } from './listSettings';
import { useShowContactsFilters } from './hooks/useShowContactsFilters';
import { IntrosRenderer } from './components/introsRenderer';

export const CompaniesList = ({ sharedList }) => {
  const {
    activeNetwork,
    listId,
    companiesColumns,
    tableRows,
    totalItems,
    handleSort,
    isLoading,
    isFetching,
    actionMenuItems,
    onColumnAction,
    hasMore,
    onLoadMore,
    queryParams,
    filtersCount,
    onSetFilters,
    onSaveFilters,
    removeContactFromSharedList,
    savedFilters,
    isSavingFilters,
    initialSortBy,
  } = useCompanyList(sharedList);
  const history = useHistory();
  const { search } = useLocation();
  const { filtersVisible, setFiltersVisible } = useShowContactsFilters();

  return (
    <Flex flexDirection="column" flexGrow="1" sx={{ zIndex: 1, position: 'relative' }}>
      {tableRows.length === 0 && !isLoading ? (
        <Box width="248px" margin="0 auto" mt="100px">
          <Image src={IconHandEmptyIcon} width="64px" maxWidth="64px" />
          <Text fontSize="14px" fontWeight="500" color="text.dark">
            Your companies list is ready
          </Text>
          <Box
            as={NavLink}
            to={`/networks/${activeNetwork.slug}/list/${listId}/contacts/settings/add-companies${search}`}
          >
            <Button
              mt="24px"
              size="small"
              variant="secondary"
              icon={Plus}
              width="100%"
              sx={{ justifyContent: 'center' }}
            >
              Add companies
            </Button>
          </Box>
        </Box>
      ) : (
        <DataTable
          itemName="company"
          isInitialized={!isLoading && !(isFetching && queryParams.page === 1)}
          onSort={handleSort}
          initialSortBy={initialSortBy}
          sortColumns={[
            {
              accessor: 'contactsCount',
              Header: 'Contacts count',
            },
          ]}
          hasMore={hasMore}
          loadMore={onLoadMore}
          canSort
          items={tableRows}
          totalItems={totalItems}
          columnDefinition={companiesColumns}
          isLoadingMore={isFetching}
          onColumnAction={onColumnAction}
          persistColumnKey={`${activeNetwork.id}-${listId}-companies-columns-v3`}
          showTableActions
          cellStyle={{
            padding: '0px',
          }}
          components={{
            companyNameRenderer: CompanyNameRenderer,
            contactNameRenderer: ContactNameRenderer,
            companyRowRenderer: CompanyRowRenderer,
            contactOfRenderer: ContactOfRenderer,
            introsRenderer: IntrosRenderer,
            groupRenderer: CompanyNameRenderer,
          }}
          filtersTitle="Filter contacts"
          filterCount={filtersCount}
          onSetFilters={onSetFilters}
          onSaveFilters={onSaveFilters}
          FiltersComponent={CompanyFilters}
          companyFilters={{
            savedFilters,
            isSavingFilters,
            showListFilters: filtersVisible,
            filtersTitle: 'Filter contacts',
            filters: queryParams.filters,
            filterCount: filtersCount,
            setFilters: onSetFilters,
            setFiltersVisible,
            saveFilters: onSaveFilters,
            filtersVisible,
          }}
          rowActions={{
            height: 330,
            items: actionMenuItems,
          }}
          sx={{
            height: 'calc(100vh - 85px)!important',
            '.ag-root-wrapper': {
              border: 'none',
            },

            '.ag-column-first': {
              paddingLeft: '8px',
            },
          }}
          tableActionsSx={{
            pr: '16px',
            pl: '8px',
            borderBottom: '1px solid',
            borderColor: 'border.subtle',
          }}
          rightActions={
            <Tooltip
              trigger={
                <Flex
                  sx={{ alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                  onClick={() => {
                    history.push(
                      `/networks/${activeNetwork.slug}/list/${listId}/contacts/export${window.location.search}`,
                    );
                    trackEvent('lists:export_csv_click', {
                      collection_id: activeNetwork.id,
                      list_slug: sharedList.slug,
                      object_type: 'companies',
                    });
                  }}
                >
                  <Box as={Download} size="16px" />
                  <Text
                    display={['none', 'none', 'block']}
                    sx={{ fontSize: '14px', fontWeight: '500', lineHeight: '16px' }}
                  >
                    Export
                  </Text>
                </Flex>
              }
              content="Export a CSV of these filtered results to your inbox"
              position="top-end"
              sx={{
                minWidth: 'max-content !important',
                '.tooltip-arrow': { left: '20px !important' },
              }}
            />
          }
          tableActions={
            <Flex
              sx={{
                flex: '0 0 auto',
                alignItems: 'center',
                p: '8px',
                gap: '8px',
                cursor: 'pointer',
                bg: filtersVisible ? 'neutral.20' : 'transparent',
              }}
              onClick={() => setFiltersVisible(!filtersVisible)}
            >
              <Box as={ListFilter} width="16px" height="16px" aria-hidden="true" />
              <Text fontSize="14px" fontWeight="500" display={['none', 'none', 'Block']}>
                Filter
              </Text>
              {filtersCount > 0 && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  px="4px"
                  width="16px"
                  height="16px"
                  sx={{ bg: 'purple.100', borderRadius: 'circle' }}
                >
                  <Text fontSize="12px" fontWeight="600" color="purple.400">
                    {filtersCount}
                  </Text>
                </Flex>
              )}
            </Flex>
          }
          gridOptions={{
            processCellForClipboard,
            getRowClass,
          }}
        />
      )}
      <Switch>
        <Route path="/networks/:slug/list/:listId/contacts/settings">
          <ListSettings sharedList={sharedList} />
        </Route>
        <Route exact path="/networks/:slug/list/:listId/contacts/export">
          <ExportListCompanies sharedList={sharedList} />
        </Route>
        <Route path="/networks/:activeNetwork/list/:listId/contacts/detail/:contactId">
          <ContactDetail
            queryParams={queryParams}
            isTalentNetworkEnabled={!activeNetwork?.tnHidden}
            list={sharedList}
            onClose={() => {
              history.push(`/networks/${activeNetwork.slug}/list/${sharedList.id}/contacts${history.location.search}`);
            }}
            deleteContactFromList={removeContactFromSharedList}
            page="companies_list"
          />
        </Route>
      </Switch>
    </Flex>
  );
};

CompaniesList.propTypes = {
  sharedList: PropTypes.object,
};

CompaniesList.defaultProps = {
  sharedList: {},
};
